/**
 * audio.ts — local narration helpers only
 * Global audio state is now owned by audioController.ts
 */

import { AudioController } from "@/utils/audioController";
import { log } from "@/utils/debug";
import { audioDir } from "@/utils/config";

// Selectors
export const NARRATION_SELECTOR = "audio[data-narration], audio[data-role='narration'], audio[data-section-audio='true']";


/**
 * Return the most visible narration element.
 */
function getMostVisibleNarration(): HTMLAudioElement | null {
  const sections = Array.from(document.querySelectorAll("section"));

  let bestEl: HTMLAudioElement | null = null;
  let bestVis = -1;

  for (const sec of sections) {
    const audio = sec.querySelector<HTMLAudioElement>(NARRATION_SELECTOR);
    if (!audio) continue;

    const rect = sec.getBoundingClientRect();
    const vh = window.innerHeight;

    const visible = Math.min(rect.bottom, vh) - Math.max(rect.top, 0);
    const score = Math.max(0, Math.min(visible, rect.height)) / (rect.height || 1);

    if (score > bestVis) {
      bestVis = score;
      bestEl = audio;
    }
  }

  // Require at least 25% visibility
  if (!bestEl || bestVis < 0.25) return null;

  return bestEl;
}

/**
 * Soft pause narration in a section.
 */
export function pauseSectionAudio(section: Element): void {
  section.querySelectorAll<HTMLAudioElement>(NARRATION_SELECTOR)
    .forEach(a => a.pause());
}

/**
 * Hard stop narration in a section.
 */
export function stopSectionAudio(section: Element): void {
  section.querySelectorAll<HTMLAudioElement>(NARRATION_SELECTOR)
    .forEach(a => {
      a.pause();
      a.currentTime = 0;
    });
}

/**
 * Attempt to play narration considering global audio permissions.
 */
export function playMostVisibleNarration(force = false): void {
  // Check global audio gate
  if (!AudioController.isAudioPlayable() && !force) return;

  const audio = getMostVisibleNarration();
  if (!audio) return;

  const alreadyPlaying = !audio.paused && !audio.ended;

  try {
    // Resume if already rolling; only restart when paused/ended.
    if (!alreadyPlaying) {
      // Reset non-looping clips to start when asked to play.
      try { if (!audio.loop) audio.currentTime = 0; } catch {}
    }
    if (alreadyPlaying && !force) return;

    audio.play().catch(err => log("[Narration Autoplay Error]", err));
  } catch (err) {
    log("[Narration Playback Error]", err);
  }
}

/**
 * Normalize a raw audio filename or partial path into a full URL.
 * Priority:
 *   1) config-level audioDir
 *   2) window.STORYCORE_BASE_URL + /audio
 *   3) fallback: /audio/filename
 */
export function resolveAudioSrc(raw: string): string {
  if (!raw) return raw;

  // Absolute URL → leave it alone
  if (/^https?:\/\//i.test(raw)) return raw;

  const filename = raw.replace(/^\//, "");

  // 1) config-level audioDir
  if (audioDir && typeof audioDir === "string") {
    return `${audioDir.replace(/\/$/, "")}/${filename}`;
  }

  // 2) Base URL (main StoryCore deployment)
  const base = (window as any).STORYCORE_BASE_URL;
  if (base && typeof base === "string") {
    return `${base.replace(/\/$/, "")}/audio/${filename}`;
  }

  // 3) Derive from current document location
  try {
    const docPath = (window.location.pathname || "").replace(/\/[^\/]*$/, "");
    if (docPath) {
      return `${docPath.replace(/\/$/, "")}/audio/${filename}`;
    }
  } catch {}

  // 4) Fallback
  return `/audio/${filename}`;
}

/**
 * Create a narration element from a src.
 */
export function createNarrationElement(src: string): HTMLAudioElement {
  const audio = document.createElement("audio");
  audio.src = resolveAudioSrc(src);
  audio.preload = "auto";
  audio.controls = false;
  audio.style.display = "none";
  audio.setAttribute("data-section-audio", "true");

  // Element muted state = global audio state
  audio.muted = AudioController.isMuted();

  return audio;
}
// Auto-resume narration when global audio is unmuted from a user-facing source.
try {
  document.addEventListener("sc:mutechange", (ev: Event) => {
    const detail = (ev as CustomEvent<any>).detail || {};
    const muted = !!detail.muted;
    const source = detail.source as string | undefined;

    // Only act when audio has just been unmuted.
    if (muted) return;

    // Ignore purely system/observer/autoplay flips so we don't surprise the user.
    if (source && (source === "system" || source === "autoplay" || source === "observer")) {
      return;
    }

    // Now that audio is allowed again, try to play narration in the most visible section.
    try {
      playMostVisibleNarration(true);
    } catch (err) {
      log("[Narration Auto-Resume Error]", err);
    }
  });
} catch {
  // In environments without a real document (SSR, tests), skip auto-resume.
}
